import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../components/Modal';
import useEscapeKey from '../hooks/useEscapeKey';
import { API_BASE } from './AdminPanel/utils/constants';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const QUARTER_TIMES = Array.from({ length: 24 * 4 }, (_, index) => {
  const hour24 = Math.floor(index / 4);
  const minute = (index % 4) * 15;
  const value = `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const suffix = hour24 < 12 ? 'AM' : 'PM';
  const hour12 = hour24 % 12 || 12;
  return { value, label: `${hour12}:${String(minute).padStart(2, '0')} ${suffix}` };
});

const isQuarterHour = (value) => QUARTER_TIMES.some((slot) => slot.value === value);

function isSoonerThanTwoDays(dateStr) {
  if (!dateStr) return false;
  const visit = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(visit.getTime())) return false;
  const earliest = new Date();
  earliest.setHours(0, 0, 0, 0);
  earliest.setDate(earliest.getDate() + 2);
  return visit < earliest;
}

const emptyVisitor = () => ({ name: '', email: '' });

const emptyForm = () => ({
  visitors: [emptyVisitor()],
  date: '',
  startTime: '',
  endTime: '',
  title: '',
  notes: '',
  attendees: [],
});

function formFromVisit(visit) {
  return {
    visitors: visit.visitors.map((v) => ({ name: v.name, email: v.email })),
    date: visit.date,
    startTime: visit.start_time,
    endTime: visit.end_time,
    title: visit.title,
    notes: visit.notes || '',
    attendees: visit.attendees.map((a) => ({ name: a.name, email: a.email })),
  };
}

async function readJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

function formIsReady(form) {
  const visitors = form.visitors.filter((v) => v.name.trim() || v.email.trim());
  if (visitors.length === 0) return false;
  if (visitors.some((v) => !v.name.trim() || !EMAIL_RE.test(v.email.trim()))) return false;
  if (!form.title.trim() || !form.date || !form.startTime || !form.endTime) return false;
  if (!isQuarterHour(form.startTime) || !isQuarterHour(form.endTime)) return false;
  if (form.endTime <= form.startTime) return false;
  return true;
}

function personLabel(name, email) {
  if (!email) return name || '';
  return name && name !== email ? `${name} (${email})` : email;
}

function visitorNames(visit) {
  return visit.visitors.map((v) => v.name).join(', ');
}

function needsAttention(visit) {
  return Boolean(visit.pending_change) || visit.status === 'saved' || visit.status === 'email_sent';
}

function statusInfo(visit) {
  if (visit.pending_change?.kind === 'cancelled') return { label: 'Cancelled – tell reception', tone: 'attention' };
  if (visit.pending_change?.kind === 'updated') return { label: 'Edited – tell reception', tone: 'attention' };
  if (visit.status === 'saved') return { label: 'Step 1 of 2 – send registration email', tone: 'attention' };
  if (visit.status === 'email_sent') return { label: 'Step 2 of 2 – send calendar invite', tone: 'attention' };
  if (visit.status === 'complete') return { label: 'Complete', tone: 'complete' };
  if (visit.status === 'cancelled') return { label: 'Cancelled', tone: 'cancelled' };
  return { label: visit.status, tone: '' };
}

function isUpcoming(visit, now) {
  if (visit.pending_change) return true;
  if (visit.status === 'cancelled') return false;
  const end = new Date(visit.end_at || visit.start_at);
  return Number.isNaN(end.getTime()) ? true : end >= now;
}

async function postProgress(visitId, action) {
  const res = await fetch(`${API_BASE}/api/visitors/${visitId}/progress`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  const data = await readJson(res);
  return data.visit;
}

export default function InviteVisitorPage({ user }) {
  const [offices, setOffices] = useState([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [officeId, setOfficeId] = useState('uk');
  const [employees, setEmployees] = useState([]);
  const [visits, setVisits] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [listTab, setListTab] = useState('upcoming');
  const [formVisit, setFormVisit] = useState(null); // null closed, {} new, visit edit
  const [guideVisit, setGuideVisit] = useState(null);

  const office = offices.find((item) => item.id === officeId) || null;

  useEffect(() => {
    let cancelled = false;
    async function loadOffices() {
      try {
        const res = await fetch(`${API_BASE}/api/visitors/offices`, { credentials: 'include' });
        const data = await readJson(res);
        if (cancelled) return;
        setOffices(data.offices || []);
        setDisclaimer(data.calendar_disclaimer || '');
        if (data.offices?.length && !data.offices.some((item) => item.id === 'uk')) {
          setOfficeId(data.offices[0].id);
        }
      } catch (err) {
        if (!cancelled) setPageError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadOffices();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadEmployees() {
      try {
        const res = await fetch(`${API_BASE}/api/org_chart/employees`, { credentials: 'include' });
        const data = await readJson(res);
        if (cancelled) return;
        const rows = Object.entries(data.employees || {}).map(([email, rec]) => ({
          email,
          name: `${rec.first_name || ''} ${rec.last_name || ''}`.trim() || email,
        }));
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setEmployees(rows);
      } catch (err) {
        console.error('Failed to load employees', err);
      }
    }
    loadEmployees();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!officeId) return undefined;
    let cancelled = false;
    async function loadVisits() {
      try {
        const res = await fetch(`${API_BASE}/api/visitors?office=${officeId}`, {
          credentials: 'include',
        });
        const data = await readJson(res);
        if (cancelled) return;
        setVisits(data.visits || []);
        setIsAdmin(Boolean(data.is_admin));
      } catch (err) {
        if (!cancelled) setPageError(err.message);
      }
    }
    loadVisits();
    return () => {
      cancelled = true;
    };
  }, [officeId]);

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const groups = { upcoming: [], past: [] };
    visits.forEach((v) => groups[isUpcoming(v, now) ? 'upcoming' : 'past'].push(v));
    groups.upcoming.sort((a, b) => {
      const attention = needsAttention(b) - needsAttention(a);
      if (attention !== 0) return attention;
      return (a.start_at || '').localeCompare(b.start_at || '');
    });
    groups.past.sort((a, b) => (b.start_at || '').localeCompare(a.start_at || ''));
    return groups;
  }, [visits]);

  const mergeVisit = (visit) => {
    setVisits((current) => {
      const exists = current.some((item) => item.id === visit.id);
      return exists
        ? current.map((item) => (item.id === visit.id ? visit : item))
        : [visit, ...current];
    });
  };

  const onSaved = (visit, wasEdit) => {
    mergeVisit(visit);
    setFormVisit(null);
    if (!wasEdit || needsAttention(visit)) {
      setGuideVisit(visit);
    }
  };

  const cancelVisit = async (visit) => {
    const ok = window.confirm(
      'Cancel this visit? You will still need to tell reception and delete the calendar event yourself.'
    );
    if (!ok) return;
    try {
      const res = await fetch(`${API_BASE}/api/visitors/${visit.id}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await readJson(res);
      mergeVisit(data.visit);
      if (data.visit.pending_change) setGuideVisit(data.visit);
    } catch (err) {
      setPageError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="page-wrap">
        <p style={{ color: 'var(--symbolica-muted)' }}>Loading offices…</p>
      </div>
    );
  }

  const list = listTab === 'upcoming' ? upcoming : past;

  return (
    <div className="page-wrap">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h1 className="content-title" style={{ marginBottom: 4 }}>Invite Visitor</h1>
          <p style={{ color: 'var(--symbolica-muted)', margin: 0, lineHeight: 1.5 }}>
            Register visitors with the building and send them a calendar invite from your own Google account.
          </p>
        </div>
        <button type="button" className="primary-button" onClick={() => setFormVisit({})}>
          New invite
        </button>
      </div>

      <div className="visitor-guidance">
        <p>
          <strong>Give reception two days.</strong> Please invite a visitor at least two days before they arrive, so the reception team has time to set the visit up.
        </p>
        <p>
          <strong>Send the invite from here.</strong> The calendar invite includes our nondisclosure and compliance terms. Your visitor has to see those before they arrive, so arrange the visit through this page rather than a separate email.
        </p>
        <p>
          <strong>There should be a business reason.</strong> A meeting, an interview, a customer, or a vendor is the usual case. An external research partnership or an internship needs approval before you invite them, and the visit still has to follow our other policies.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '16px 0', flexWrap: 'wrap' }}>
        {offices.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`secondary-button ${item.id === officeId ? 'active' : ''}`}
            onClick={() => setOfficeId(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {pageError && <Banner tone="error">{pageError}</Banner>}

      <div className="visitor-tabs">
        <button
          type="button"
          className={`visitor-tab ${listTab === 'upcoming' ? 'active' : ''}`}
          onClick={() => setListTab('upcoming')}
        >
          Upcoming{upcoming.length ? ` (${upcoming.length})` : ''}
        </button>
        <button
          type="button"
          className={`visitor-tab ${listTab === 'past' ? 'active' : ''}`}
          onClick={() => setListTab('past')}
        >
          Past{past.length ? ` (${past.length})` : ''}
        </button>
      </div>

      <p style={{ color: 'var(--symbolica-muted)', fontSize: 13, marginTop: 0 }}>
        {isAdmin ? `Every visit to the ${office?.label || 'office'}.` : `Visits you are hosting at the ${office?.label || 'office'}.`}
      </p>

      {list.length === 0 ? (
        <div className="placeholder-card">
          <h3>{listTab === 'upcoming' ? 'No upcoming visits' : 'No past visits'}</h3>
          <p>
            {listTab === 'upcoming'
              ? 'Use New invite to register a visitor and send them a calendar invite.'
              : 'Completed and cancelled visits will show up here.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map((visit) => (
            <VisitCard
              key={visit.id}
              visit={visit}
              isAdmin={isAdmin}
              onContinue={() => setGuideVisit(visit)}
              onEdit={() => setFormVisit(visit)}
              onCancel={() => cancelVisit(visit)}
            />
          ))}
        </div>
      )}

      {formVisit !== null && (
        <VisitFormModal
          user={user}
          office={office}
          employees={employees}
          visit={formVisit.id ? formVisit : null}
          onClose={() => setFormVisit(null)}
          onSaved={onSaved}
        />
      )}

      {guideVisit && (
        <GuideModal
          visit={visits.find((v) => v.id === guideVisit.id) || guideVisit}
          disclaimer={guideVisit.calendar_disclaimer || disclaimer}
          onUpdate={mergeVisit}
          onClose={() => setGuideVisit(null)}
        />
      )}
    </div>
  );
}

function VisitCard({ visit, isAdmin, onContinue, onEdit, onCancel }) {
  const status = statusInfo(visit);
  const attention = needsAttention(visit);
  const cancelled = visit.status === 'cancelled';
  const continueLabel = visit.pending_change ? 'Tell reception' : 'Continue setup';

  return (
    <div className={`visitor-card ${attention ? 'attention' : ''}`}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
          <strong style={{ fontSize: 15 }}>{visit.title}</strong>
          <span className={`visitor-pill ${status.tone}`}>{status.label}</span>
        </div>
        <div style={{ fontSize: 14, marginBottom: 2 }}>{visit.arrival_text}</div>
        <div style={{ fontSize: 13, color: 'var(--symbolica-muted)' }}>
          {visitorNames(visit)}
          {isAdmin && ` · Host: ${visit.host_name}`}
        </div>
      </div>
      <div className="visitor-card-actions">
        {attention && (
          <button type="button" className="primary-button" onClick={onContinue}>{continueLabel}</button>
        )}
        {!attention && !cancelled && (
          <button type="button" className="secondary-button" onClick={onContinue}>View</button>
        )}
        {!cancelled && (
          <>
            <button type="button" className="secondary-button" onClick={onEdit}>Edit</button>
            <button type="button" className="secondary-button" onClick={onCancel}>Cancel visit</button>
          </>
        )}
      </div>
    </div>
  );
}

function VisitFormModal({ user, office, employees, visit, onClose, onSaved }) {
  const editing = Boolean(visit);
  const [form, setForm] = useState(() => (visit ? formFromVisit(visit) : emptyForm()));
  const [attendeeEmail, setAttendeeEmail] = useState('');
  const [linkCheck, setLinkCheck] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEscapeKey(onClose);

  const ready = formIsReady(form);
  const payload = useMemo(() => ({
    office_id: office?.id,
    visitors: form.visitors
      .filter((v) => v.name.trim() || v.email.trim())
      .map((v) => ({ name: v.name.trim(), email: v.email.trim() })),
    attendees: form.attendees,
    title: form.title.trim(),
    notes: form.notes,
    date: form.date,
    start_time: form.startTime,
    end_time: form.endTime,
  }), [office, form]);

  useEffect(() => {
    if (!ready) {
      setLinkCheck(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/visitors/link-check`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await readJson(res);
        if (!cancelled) setLinkCheck(data);
      } catch (err) {
        if (!cancelled) setLinkCheck(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ready, payload]);

  const updateVisitor = (index, field, value) => {
    setForm((current) => {
      const visitors = current.visitors.slice();
      visitors[index] = { ...visitors[index], [field]: value };
      return { ...current, visitors };
    });
  };

  const addAttendee = () => {
    const email = attendeeEmail.trim().toLowerCase();
    if (!email) return;
    const match = employees.find((emp) => emp.email === email);
    if (!match) return;
    setForm((current) => {
      if (current.attendees.some((att) => att.email === email)) return current;
      return { ...current, attendees: [...current.attendees, match] };
    });
    setAttendeeEmail('');
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!ready) {
      if (form.startTime && form.endTime && (!isQuarterHour(form.startTime) || !isQuarterHour(form.endTime))) {
        setError('Start and end times must be on the quarter hour (:00, :15, :30, or :45).');
      } else if (form.endTime && form.startTime && form.endTime <= form.startTime) {
        setError('End time must be after start time.');
      } else {
        setError('Add each visitor’s name and email, a title, and a start time before the end time.');
      }
      return;
    }
    setSaving(true);
    try {
      const url = editing ? `${API_BASE}/api/visitors/${visit.id}` : `${API_BASE}/api/visitors`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await readJson(res);
      onSaved(data.visit, editing);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const hostLabel = editing
    ? personLabel(visit.host_name, visit.host_email)
    : personLabel(user?.name, user?.email);

  return (
    <Modal isOpen onClose={onClose} className="visitor-form-modal">
      <form onSubmit={submit}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 500, margin: 0 }}>
            {editing ? 'Edit visit' : `New invite – ${office?.label || ''}`}
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <p style={{ color: 'var(--symbolica-muted)', fontSize: 13, margin: '0 0 16px' }}>
          Date and times are in {office?.timezone_label}.
          {editing && ' If reception has already been told, saving drafts an update email for you to send.'}
        </p>

        <div className="form-group">
          <label>Visitors</label>
          {form.visitors.map((visitor, index) => (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                placeholder="Full name"
                value={visitor.name}
                onChange={(e) => updateVisitor(index, 'name', e.target.value)}
                aria-label={`Visitor ${index + 1} full name`}
              />
              <input
                type="email"
                placeholder="Email"
                value={visitor.email}
                onChange={(e) => updateVisitor(index, 'email', e.target.value)}
                aria-label={`Visitor ${index + 1} email`}
              />
              <button
                type="button"
                className="secondary-button"
                disabled={form.visitors.length === 1}
                onClick={() => setForm((current) => ({
                  ...current,
                  visitors: current.visitors.filter((_, i) => i !== index),
                }))}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="secondary-button"
            onClick={() => setForm((current) => ({ ...current, visitors: [...current.visitors, emptyVisitor()] }))}
          >
            Add another visitor
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div className="form-group">
            <label htmlFor="visit-date">Date</label>
            <input id="visit-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="form-group">
            <label htmlFor="visit-start">Start time</label>
            <TimeSelect id="visit-start" value={form.startTime} onChange={(startTime) => setForm({ ...form, startTime })} />
          </div>
          <div className="form-group">
            <label htmlFor="visit-end">End time</label>
            <TimeSelect id="visit-end" value={form.endTime} onChange={(endTime) => setForm({ ...form, endTime })} />
          </div>
        </div>
        {linkCheck?.arrival_text && (
          <p style={{ marginTop: -8, color: 'var(--symbolica-muted)', fontSize: 13 }}>
            Reception will be told: {linkCheck.arrival_text}.
          </p>
        )}
        {isSoonerThanTwoDays(form.date) && (
          <p style={{ marginTop: -4, color: '#92400e', fontSize: 13 }}>
            This visit is less than two days away. Reception asks for at least two days to set a visit up.
          </p>
        )}

        <div className="form-group">
          <label htmlFor="visit-title">Meeting title</label>
          <input id="visit-title" type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>

        <div className="form-group">
          <label htmlFor="visit-notes">Notes or agenda (optional)</label>
          <textarea id="visit-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        {linkCheck?.link_warning && <LinkWarning check={linkCheck} />}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div className="form-group">
            <label>Host</label>
            <input type="text" value={hostLabel} readOnly />
          </div>
          <div className="form-group">
            <label htmlFor="visit-attendee">Other Symbolica attendees</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                id="visit-attendee"
                value={attendeeEmail}
                onChange={(e) => setAttendeeEmail(e.target.value)}
                style={{ flex: 1, minWidth: 0 }}
              >
                <option value="">Select a colleague</option>
                {employees
                  .filter((emp) => emp.email !== (user?.email || '').toLowerCase())
                  .filter((emp) => !form.attendees.some((att) => att.email === emp.email))
                  .map((emp) => (
                    <option key={emp.email} value={emp.email}>{emp.name}</option>
                  ))}
              </select>
              <button type="button" className="secondary-button" onClick={addAttendee}>Add</button>
            </div>
          </div>
        </div>
        {form.attendees.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: -8, marginBottom: 16 }}>
            {form.attendees.map((att) => (
              <span key={att.email} className="visitor-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {att.name}
                <button
                  type="button"
                  className="modal-close-btn"
                  style={{ fontSize: 13, padding: 0 }}
                  aria-label={`Remove ${att.name}`}
                  onClick={() => setForm((current) => ({
                    ...current,
                    attendees: current.attendees.filter((item) => item.email !== att.email),
                  }))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {office && (
          <div className="form-group">
            <label>Entrance instructions sent to visitors</label>
            <div className="visitor-callout">
              <div style={{ marginBottom: 6, fontWeight: 500 }}>{office.address}</div>
              {office.entrance_instructions}
            </div>
          </div>
        )}

        {error && <Banner tone="error">{error}</Banner>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" type="submit" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Save and continue'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TimeSelect({ id, value, onChange }) {
  const options = value && !isQuarterHour(value)
    ? [{ value, label: value }, ...QUARTER_TIMES]
    : QUARTER_TIMES;
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select a time</option>
      {options.map((slot) => (
        <option key={slot.value} value={slot.value}>{slot.label}</option>
      ))}
    </select>
  );
}

function guideStep(visit) {
  if (visit.pending_change) return 'change';
  if (visit.status === 'cancelled') return 'cancelled';
  if (visit.status === 'saved') return 'email';
  if (visit.status === 'email_sent') return 'calendar';
  return 'done';
}

function GuideModal({ visit, disclaimer, onUpdate, onClose }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const step = guideStep(visit);
  const names = visitorNames(visit);

  const progress = async (action) => {
    setBusy(action);
    setError('');
    try {
      onUpdate(await postProgress(visit.id, action));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const openLink = (url, action) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    progress(action);
  };

  // Step 1 (details) is always done by the time this modal opens.
  const currentStep = { email: 2, calendar: 3, done: 4 }[step] ?? null;

  return (
    <Modal isOpen onClose={() => {}} className="visitor-guide-modal">
      <div className="visitor-guide-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--symbolica-muted)' }}>{visit.arrival_text}</div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>{visit.title} · {names}</div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        {currentStep !== null && (
          <div className="visitor-stepper">
            <Step n={1} label="Visit details" current={currentStep} />
            <Step n={2} label="Register with the building" current={currentStep} />
            <Step n={3} label="Send the calendar invite" current={currentStep} />
          </div>
        )}
      </div>

      <div className="visitor-guide-body">
        {step === 'email' && (
          <>
            <h2 className="visitor-guide-title">Next step: register your visitor with the building</h2>
            <p className="visitor-guide-lead">
              So {names} can get past reception, the building needs a registration email from you.
              The email is already written and addressed. Open it in Gmail and press Send.
            </p>
            <div>
              <button
                type="button"
                className="primary-button visitor-big-button"
                onClick={() => openLink(visit.gmail_url, 'email_opened')}
                disabled={busy === 'email_opened'}
              >
                Open the registration email in Gmail
              </button>
            </div>
            <div className="visitor-callout">
              To: reception · Subject: {visit.registration_subject}
            </div>
            {visit.link_warning && <LinkWarning check={visit} />}
            <p style={{ margin: 0, fontSize: 14, color: 'var(--symbolica-muted)' }}>
              Once it has sent, come back to this tab and confirm below to move on to the calendar invite.
            </p>
          </>
        )}

        {step === 'calendar' && (
          <>
            <h2 className="visitor-guide-title">Last step: send the calendar invite</h2>
            <p className="visitor-guide-lead">
              Your visitors get the address, entrance instructions, and our visitor terms through this invite.
              Open the prefilled event in Google Calendar, check the guests, and save it.
            </p>
            <div>
              <button
                type="button"
                className="primary-button visitor-big-button"
                onClick={() => openLink(visit.calendar_url, 'calendar_opened')}
                disabled={busy === 'calendar_opened'}
              >
                Open the invite in Google Calendar
              </button>
            </div>
            <div className="visitor-callout warn">{disclaimer}</div>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--symbolica-muted)' }}>
              Once the event is saved, come back and confirm below.
            </p>
          </>
        )}

        {step === 'done' && (
          <>
            <h2 className="visitor-guide-title">All set</h2>
            <p className="visitor-guide-lead">
              Reception has the registration and {names} {visit.visitors.length === 1 ? 'has' : 'have'} the calendar invite.
              If anything changes, use Edit or Cancel visit on the visit card and we will draft the follow-up email for reception.
            </p>
            <div className="visitor-callout">
              <div><strong>When:</strong> {visit.arrival_text}</div>
              <div><strong>Visitors:</strong> {names}</div>
              <div><strong>Host:</strong> {visit.host_name}</div>
            </div>
          </>
        )}

        {step === 'change' && (
          <>
            <h2 className="visitor-guide-title">
              {visit.pending_change.kind === 'cancelled'
                ? 'Tell reception the visit is cancelled'
                : 'Tell reception about the change'}
            </h2>
            <p className="visitor-guide-lead">
              {visit.pending_change.kind === 'cancelled'
                ? 'Reception already has a registration for this visit. Send them the cancellation from your Gmail so they stop expecting your visitor.'
                : 'Reception already has a registration for this visit. Send them the update from your Gmail so they have the right details.'}
            </p>
            <div>
              <button
                type="button"
                className="primary-button visitor-big-button"
                onClick={() => openLink(visit.change_gmail_url, 'change_email_opened')}
                disabled={busy === 'change_email_opened'}
              >
                Open the email in Gmail
              </button>
            </div>
            <div className="visitor-callout">
              Subject: {visit.change_subject}
            </div>
            <div className="visitor-callout warn">
              {visit.pending_change.kind === 'cancelled'
                ? 'Then delete the event in your Google Calendar. That is what tells your visitors the visit is off.'
                : 'Then open the event in your Google Calendar and update it. That is what tells your visitors about the new details.'}
            </div>
            {visit.change_link_warning && (
              <LinkWarning check={{
                link_warning: visit.change_link_warning,
                registration_subject: visit.change_subject,
                registration_body: visit.change_body,
              }} />
            )}
          </>
        )}

        {step === 'cancelled' && (
          <>
            <h2 className="visitor-guide-title">This visit is cancelled</h2>
            <p className="visitor-guide-lead">
              If you created a calendar event for it, delete it so your visitors are told.
            </p>
          </>
        )}

        {error && <Banner tone="error">{error}</Banner>}
      </div>

      <div className="visitor-guide-footer">
        {step === 'done' || step === 'cancelled' ? (
          <span />
        ) : (
          <button type="button" className="secondary-button" onClick={onClose}>Finish later</button>
        )}
        {step === 'email' && (
          <button type="button" className="primary-button" onClick={() => progress('email_sent')} disabled={!!busy}>
            I’ve sent the email – next step
          </button>
        )}
        {step === 'calendar' && (
          <button type="button" className="primary-button" onClick={() => progress('complete')} disabled={!!busy}>
            I’ve saved the calendar event – finish
          </button>
        )}
        {step === 'change' && (
          <button type="button" className="primary-button" onClick={() => progress('change_email_sent')} disabled={!!busy}>
            I’ve sent it to reception
          </button>
        )}
        {(step === 'done' || step === 'cancelled') && (
          <button type="button" className="primary-button" onClick={onClose}>Done</button>
        )}
      </div>
    </Modal>
  );
}

function Step({ n, label, current }) {
  const state = n < current ? 'done' : n === current ? 'active' : '';
  return (
    <div className={`visitor-step ${state}`}>
      <span className="visitor-step-num">
        {state === 'done' ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : n}
      </span>
      <span>{label}</span>
    </div>
  );
}

function LinkWarning({ check }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const text = `${check.registration_subject}\n\n${check.registration_body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <div className="visitor-callout warn">
      <div>{check.link_warning}</div>
      {check.registration_body && (
        <>
          <p style={{ marginBottom: 8 }}>If the Gmail tab does not open, copy the email and send it yourself.</p>
          <textarea
            readOnly
            value={`${check.registration_subject}\n\n${check.registration_body}`}
            style={{
              width: '100%',
              minHeight: 120,
              boxSizing: 'border-box',
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              padding: 10,
              border: '1px solid var(--symbolica-warm-border)',
              borderRadius: 'var(--radius-sm)',
            }}
          />
          <button type="button" className="secondary-button" onClick={copy} style={{ marginTop: 8 }}>
            {copied ? 'Copied' : 'Copy email'}
          </button>
        </>
      )}
    </div>
  );
}

function Banner({ children, tone }) {
  const error = tone === 'error';
  return (
    <div style={{
      background: error ? '#fee2e2' : '#FEF3C7',
      border: `1px solid ${error ? '#fca5a5' : '#FCD34D'}`,
      borderRadius: 'var(--radius-sm)',
      padding: 12,
      marginBottom: 12,
      color: error ? '#7f1d1d' : '#78350f',
      fontSize: 14,
    }}>
      {children}
    </div>
  );
}
